import type { VariantProps } from 'class-variance-authority';
import { memo, useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';

import { ctw } from '@/common';
import { Check, Copy } from '../../../../common/icons/lucide-adapter';

const sizeVariants = cva(null, {
  variants: {
    size: {
      small: 'h-2 w-2',
      default: 'h-3 w-3',
      large: 'h-4 w-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

type CopyButtonProps = {
  textToCopy: string;
  className?: string;
} & VariantProps<typeof sizeVariants>;

export const CopyButton = memo<CopyButtonProps>(({ textToCopy, className, size }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  useEffect(() => {
    if (!copied) return;

    const timeout = setTimeout(() => {
      setCopied(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [copied]);

  const label = copied ? 'Copied!' : 'Copy to clipboard';

  return (
    <button
      onClick={handleCopy}
      className={ctw('text-gray-400 transition-colors hover:text-gray-600', className)}
      title={label}
      aria-label={label}
    >
      {copied ? (
        <Check className={ctw(sizeVariants({ size }), 'text-green-500')} />
      ) : (
        <Copy className={ctw(sizeVariants({ size }))} />
      )}
    </button>
  );
});

CopyButton.displayName = 'CopyButton';
